"use client";

import { useContext, useEffect, useId, useRef, useState } from "react";
import { languageContext } from "@/contexts/language-context";
import { useRv2T } from "@/lib/rewards-v2/use-rv2-t";

const LANGS = [
  { code: "fr", flag: "🇫🇷", nameKey: "nav.lang.fr" },
  { code: "en", flag: "🇬🇧", nameKey: "nav.lang.en" },
  { code: "ar", flag: "🇲🇦", nameKey: "nav.lang.ar" },
] as const;

type RewardsV2LangSwitcherProps = {
  /** Slightly smaller padding for checkout / creation-login headers */
  compact?: boolean;
  className?: string;
  onLanguageChange?: () => void;
};

export default function RewardsV2LangSwitcher({
  compact = false,
  className = "",
  onLanguageChange,
}: RewardsV2LangSwitcherProps) {
  const t = useRv2T();
  const ctx = useContext(languageContext);
  const { lang, setLang } = ctx ?? { lang: "fr", setLang: () => {} };
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const currentLang = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const buttonPadding = compact ? "px-3 py-2" : "px-[13px] py-[11px]";

  return (
    <div ref={rootRef} className={`relative ${className}`.trim()}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-1.5 rounded-full bg-white border-[1.5px] border-[#E4E2DB] font-bold text-sm text-[#1A1A1A] hover:border-[#C9C7BF] transition-colors ${buttonPadding}`}
      >
        <span className="text-base leading-none">{currentLang.flag}</span>
        {currentLang.code.toUpperCase()}
        <span className="inline-block w-1.5 h-1.5 border-r-2 border-b-2 border-[#1A1A1A] rotate-45 -translate-y-0.5" />
      </button>
      {open && (
        <div
          id={listId}
          role="listbox"
          className="absolute top-[calc(100%+8px)] right-0 bg-white border border-[#E7E5DF] rounded-[14px] shadow-xl p-1.5 min-w-[168px] z-[60]"
        >
          {LANGS.map((lo) => (
            <button
              key={lo.code}
              type="button"
              role="option"
              aria-selected={lang === lo.code}
              onClick={() => {
                setLang(lo.code);
                setOpen(false);
                onLanguageChange?.();
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold ${
                lang === lo.code ? "bg-[#F6F5F1]" : "hover:bg-[#F6F5F1]"
              }`}
            >
              <span className="text-[17px] leading-none">{lo.flag}</span>
              {t(lo.nameKey)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { LANGS as REWARDS_V2_LANGS };
